import { FormControlLabel, Paper } from '@mui/material';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';
import { FormikTextField } from '../../../common/formik-material-ui';
import { FormikSwitch } from '../../../common/formik-material-ui/FormikSwitch';
import {
  trackSaveAddConference,
  trackSaveEditConference,
} from '../../../common/userTracking';
import { user } from '../../../recoil/atoms/user';
import { refetchToggle } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from './ModalWithStyles';

export function ConferenceModal({
  show,
  handleClose,
  methodNameEnum,
  enqueueSnackbarWithStyles,
  refetchBusinessNumbers,
  id,
  conferences,
}) {
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  const addConferenceInitialValues = {
    submitConferencePayload: {
      name: '',
      playExit: false,
      playEntrance: false,
      playNumberAttendees: false,
    },
  };
  const setRefetchToggleForNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_NUMBERS)
  );
  const setRefetchToggleForBusinessConferences = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_CONFERENCES)
  );
  const conferenceSchema = useMemo(
    () =>
      Yup.object({
        submitConferencePayload: Yup.object({
          name: Yup.string().required('Required'),
        }),
      }),
    []
  );
  const editConferenceInitialValuesMemo = useMemo(() => {
    if (conferences.state === 'hasValue') {
      const found = conferences.contents?.response?.data?.result?.find(
        (c) => Number(c.conferenceid) === Number(id)
      );
      return {
        submitConferencePayload: {
          name: found?.name,
          playExit: found?.playExit === 1,
          playEntrance: found?.playEntrance === 1,
          playNumberAttendees: found?.playNumberAttendees === 1,
        },
      };
    } else {
      return addConferenceInitialValues;
    }
  }, [conferences, id]);
  return (
    <Paper elevation={0}>
      <Formik
        enableReinitialize={true}
        validationSchema={conferenceSchema}
        onSubmit={async (values, actions) => {
          const {
            name,
            playExit,
            playEntrance,
            playNumberAttendees,
          } = values.submitConferencePayload;
          const data = {
            call: id
              ? methodNameEnum.EDIT_CONFERENCE
              : methodNameEnum.SUBMIT_NEW_CONFERENCE,
            apikey: userAtom.contents?.apikey,
            businessid: userAtom.contents?.businessid,
            name,
            playExit: playExit ? 1 : 0,
            playEntrance: playEntrance ? 1 : 0,
            playNumberAttendees: playNumberAttendees ? 1 : 0,
            conferenceid: id,
          };
          const response = await mutation.mutateAsync({
            endpoint: id
              ? endpointEnum.EDIT_CONFERENCE
              : endpointEnum.SUBMIT_NEW_CONFERENCE,
            payload: data,
          });
          const resSuccess = response?.data?.success;
          const resMessage = response?.data?.result?.code;

          if (!resSuccess) {
            enqueueSnackbarWithStyles({
              message:
                resMessage === 'ER_DUP_ENTRY'
                  ? 'Please choose a Conference Name that is not already in use'
                  : `Encountered an error trying to add ${name} Webhook`,
              alertProps: {
                severity: 'error',
              },
            });
            return;
          }

          if (response.status === 200) {
            enqueueSnackbarWithStyles({
              message: `Successfully ${
                id ? 'Edited' : 'Added'
              } ${name} Conference`,
              alertProps: {
                severity: 'success',
              },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: `Encountered an error trying to ${
                id ? 'edit' : 'add'
              } ${name} conference`,
              alertProps: {
                severity: 'warning',
              },
            });
          }
          setRefetchToggleForBusinessConferences({});
          handleClose();
          refetchBusinessNumbers();
          setRefetchToggleForNumbers({});
          actions.resetForm({});
        }}
        initialValues={
          id ? editConferenceInitialValuesMemo : addConferenceInitialValues
        }
      >
        {(formik) => (
          <>
            <form
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
              noValidate
              autoComplete="off"
            >
              <ModalWithStyles
                onSubmit={() => {
                  id ? trackSaveAddConference() : trackSaveEditConference();
                  formik.handleSubmit();
                }}
                title={id ? 'Edit Conference' : 'Add New Conference'}
                aria-labelledby="example-modal-sizes-title-md"
                show={show}
                onHide={handleClose}
                isSubmitting={formik.isSubmitting}
                isLoading={formik.isSubmitting}
                disableSubmit={!formik.dirty || !formik.isValid}
                size="md"
              >
                {/* <Modal.Body> */}
                <div className="container mt-5">
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Conference Name</h5>
                      <p className="fieldDescription">
                        Enter a unique name for your conference call. This name
                        helps agents on your account identify the conference and
                        can be used for reference in call records.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        id="standard-basic"
                        label="Conference Name"
                        name="submitConferencePayload.name"
                        formik={formik}
                        size="small"
                        variant="outlined"
                        inputProps={{ maxLength: 150 }}
                        required
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Play Exit Sound</h5>
                      <p className="fieldDescription">
                        Enable this option to play a notification sound when a
                        participant leaves the conference. This helps call
                        participants know when another person has left.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="submitConferencePayload.playExit"
                            formik={formik}
                            color="primary"
                            type="checkbox"
                          />
                        }
                        label="Play Exit Sound"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Play Entrance Sound</h5>
                      <p className="fieldDescription">
                        Enable this option to play a notification sound when a
                        participant joins the conference. This keeps everyone
                        aware of new arrivals during the call.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="submitConferencePayload.playEntrance"
                            formik={formik}
                            color="primary"
                            type="checkbox"
                          />
                        }
                        label="Play Entrance Sound"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Play # of Attendees</h5>
                      <p className="fieldDescription">
                        Enable this option to announce the number of attendees
                        to new participants when they join the conference. This
                        provides context on how many people are already on the
                        call.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="submitConferencePayload.playNumberAttendees"
                            formik={formik}
                            color="primary"
                            type="checkbox"
                          />
                        }
                        label="Play # of Attendees"
                      />
                    </div>
                  </div>
                </div>
                <br />
              </ModalWithStyles>
            </form>
          </>
        )}
      </Formik>
    </Paper>
  );
}
